import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { colors, pick, roomsForCrew, roomTaskOptions, type Frequency, type Weekday } from '@hogar/shared';
import { Button, Card, ErrorText, Field, Label, Screen, Txt, useT } from '../components/ui';
import { BackHeader } from '../components/BackHeader';
import { DaysPicker, TimeStepper } from '../components/forms';
import { roomIcons } from '../components/icons';
import { Tick } from '../components/Tick';
import { api } from '../lib/api';
import { useSetSnapshot } from '../lib/session';
import { setCrew } from '../lib/crew';
import type { Snapshot } from '@hogar/shared';

const FREQ_KEY: Record<Frequency, 'freqDaily' | 'freqTwice' | 'freqWeekly'> = { daily: 'freqDaily', twice: 'freqTwice', weekly: 'freqWeekly' };
const gardenAreas = roomsForCrew('garden');

/**
 * Adding the first gardener (Mike, 2026-10-14): the garden's days and hours, the areas the gardener
 * looks after with their starter tasks, and the gardener's name. The gardener sees only these, and
 * the garden gets its own report. Opened from Más with the name already typed, or with an existing
 * staff member to move to the garden.
 */
export default function GardenSetup() {
  const params = useLocalSearchParams<{ name?: string; staffId?: string; lang?: string }>();
  const { t, lang } = useT();
  const setSnapshot = useSetSnapshot();
  const [name, setName] = useState(params.name ?? '');
  const [workDays, setWorkDays] = useState<Weekday[]>([5]);
  const [workStart, setWorkStart] = useState('08:00');
  const [workEnd, setWorkEnd] = useState('12:00');
  const [picked, setPicked] = useState<string[]>(gardenAreas.filter((r) => r.common).map((r) => r.id));
  // Each area's starter tasks begin ticked; ideas from the other garden areas begin unticked.
  const [off, setOff] = useState<Record<string, number[]>>(() =>
    Object.fromEntries(gardenAreas.map((r) => [r.id, roomTaskOptions(r.id).flatMap((o, i) => (o.on ? [] : [i]))])),
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const snap = await api<Snapshot>('POST', '/garden-setup', {
        workDays,
        workStart,
        workEnd,
        rooms: picked.map((id) => {
          const r = gardenAreas.find((x) => x.id === id)!;
          return { catalogId: id, name: { en: r.name.en, es: r.name.es }, taskIndexes: roomTaskOptions(id).map((_, i) => i).filter((i) => !off[id].includes(i)) };
        }),
        staffFirstName: params.staffId ? undefined : name.trim() || undefined,
        staffLang: params.lang === 'en' ? 'en' : 'es',
      });
      // An existing staff member moves to the garden.
      if (params.staffId) await api('PATCH', `/staff/${params.staffId}`, { crew: 'garden' });
      setSnapshot(params.staffId ? await api<Snapshot>('GET', '/snapshot') : snap);
      setCrew('garden');
      router.replace('/rutinas');
    } catch {
      setError(t('errGeneric'));
      setBusy(false);
    }
  };

  return (
    <Screen>
      <BackHeader title={t('gardenSetupTitle')} fallback="/mas" />
      <Txt color={colors.textMuted} style={{ marginBottom: 6 }}>
        {t('gardenSetupSub')}
      </Txt>

      {!params.staffId && (
        <>
          <Label>{t('gardenerName')}</Label>
          <Field value={name} onChange={setName} placeholder="Juan" />
        </>
      )}

      <Label>{t('days')}</Label>
      <DaysPicker value={workDays} onChange={setWorkDays} lang={lang} />
      <Label>{t('startTime')}</Label>
      <TimeStepper value={workStart} onChange={setWorkStart} />
      <Label>{t('endTime')}</Label>
      <TimeStepper value={workEnd} onChange={setWorkEnd} />

      <Label>{t('gardenAreas')}</Label>
      {gardenAreas.map((r) => {
        const on = picked.includes(r.id);
        const Icon = roomIcons[r.icon];
        const options = roomTaskOptions(r.id);
        return (
          <Card key={r.id}>
            <Pressable onPress={() => setPicked(on ? picked.filter((x) => x !== r.id) : [...picked, r.id])} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 56 }}>
              <Icon size={22} color={colors.text} strokeWidth={1.6} />
              <Txt weight="semibold" style={{ flex: 1 }}>
                {pick(r.name, lang)}
              </Txt>
              <Tick on={on} />
            </Pressable>
            {on &&
              options.map(({ task, on: suggested }, i) => {
                const keep = !off[r.id].includes(i);
                const firstIdea = !suggested && (i === 0 || options[i - 1].on);
                return (
                  <View key={i}>
                    {firstIdea && (
                      <Txt size={13} weight="medium" color={colors.textMuted} style={{ paddingTop: 12, paddingBottom: 4 }}>
                        {t('moreIdeasRoom')}
                      </Txt>
                    )}
                    <Pressable
                      onPress={() => setOff({ ...off, [r.id]: keep ? [...off[r.id], i] : off[r.id].filter((x) => x !== i) })}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 52, borderTopWidth: 1, borderTopColor: colors.divider }}
                    >
                      <Tick on={keep} />
                      <View style={{ flex: 1 }}>
                        <Txt color={keep ? colors.text : colors.textMuted}>{pick(task.name, lang)}</Txt>
                        <Txt size={13} color={colors.textMuted}>
                          {task.minutes} min · {t(FREQ_KEY[task.frequency])}
                        </Txt>
                      </View>
                    </Pressable>
                  </View>
                );
              })}
          </Card>
        );
      })}

      <ErrorText>{error}</ErrorText>
      <Button
        label={t('saveGarden')}
        onPress={save}
        disabled={busy || workDays.length === 0 || workEnd <= workStart || picked.length === 0 || (!params.staffId && !name.trim())}
        style={{ marginTop: 14 }}
      />
    </Screen>
  );
}

